import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Layers, TrendingUp } from "lucide-react";
import { money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export const StackCard = ({ stack, index = 0 }) => {
  const { addItem, setOpen } = useCart();

  const addStack = () => {
    stack.products.forEach((p) =>
      addItem({ handle: p.handle, name: p.name, dose: p.dose, price: p.price, images: [p.image] }, 1)
    );
    toast.success(
      `${stack.name} added — ${stack.discount_percent}% saving applied at checkout`
    );
    setOpen(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
      className="group relative bg-ink-800 border border-[#27272A] hover:border-accent/60 transition-colors flex flex-col overflow-hidden"
      data-testid={`stack-card-${stack.slug}`}
    >
      <Link to={`/bundle/${stack.slug}`} className="relative h-44 overflow-hidden clip-corner block group/img">
        <img
          src={stack.image}
          alt={stack.name}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
        <span className="absolute top-3 left-3 flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-white bg-ink-900/70 backdrop-blur px-2.5 py-1 border border-white/10">
          <Layers size={11} /> {stack.purpose}
        </span>
        {stack.discount_percent > 0 && (
          <span className="absolute top-3 right-3 flex items-center gap-1 bg-accent text-white font-mono-tech text-[10px] font-bold tracking-widest px-2.5 py-1">
            <TrendingUp size={11} /> {stack.discount_percent}% OFF
          </span>
        )}
        <h3 className="absolute bottom-3 left-4 right-4 font-display font-black text-2xl tracking-tight leading-none">
          {stack.name}
        </h3>
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <p className="text-sm text-zinc-400 leading-relaxed">{stack.tagline}</p>

        <ul className="mt-4 space-y-2 flex-1">
          {stack.products.map((p) => (
            <li key={p.handle} className="flex items-center gap-3">
              <img
                src={p.image}
                alt={p.name}
                className="h-9 w-9 object-cover bg-ink-700 border border-[#27272A]"
              />
              <span className="font-mono-tech text-[11px] tracking-wide text-zinc-300 flex-1 truncate">
                {p.name} · {p.dose}
              </span>
              <span className="font-mono-tech text-[11px] text-zinc-500">{money(p.price)}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 pt-4 border-t border-[#27272A] flex items-end justify-between">
          <div>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 block">
              Stack price
            </span>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black text-2xl">{money(stack.total)}</span>
              {stack.discount_percent > 0 && (
                <span className="font-mono-tech text-xs text-zinc-600 line-through">
                  {money(stack.subtotal)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={addStack}
            className="flex items-center gap-1.5 bg-white text-ink-900 hover:bg-accent hover:text-white font-display font-bold text-sm uppercase tracking-wide px-4 py-2.5 transition-colors"
            data-testid={`add-stack-${stack.slug}`}
          >
            Add Stack <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
